import { useState } from "react";
import { Check, ChevronDown } from "untitledui-js-base";

import { GROUP_ROLE_LABELS } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/constants";
import {
  FieldName,
  FieldType,
  GroupRole,
  Operator,
  Rule,
  TimeFrame
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { normalizeRuleGroupRoles } from "shared/app/VerificationTemplate/helpers";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import FailedIcon from "shared/icons/status-icons/fail-icon.svg?react";
import { ApplicantType } from "shared/types";
import { cx } from "shared/utils";
import { getFullName } from "shared/utils/get-full-name";
import { formatTitleFirstWord } from "utils/formatTitle";
import { Applicant } from "types/application";
import {
  formatConditionSubTitle,
  formatConditionTitle,
  formatThreshold
} from "../utils";

interface ApplicantDetailRow {
  id: number;
  name: string;
  role: string;
  value: string | number;
}

const OPERATOR_LABELS: Record<string, string> = {
  [Operator.GreaterThan]: "greater than",
  [Operator.GreaterEqualThen]: "greater than or equal to",
  [Operator.LessThan]: "less than",
  [Operator.LessEqualThen]: "less than or equal to",
  [Operator.Equal]: "equal to",
  [Operator.NotEqual]: "not equal to"
};

const ROLE_MAP: Record<GroupRole, ApplicantType | null> = {
  [GroupRole.All]: null,
  [GroupRole.Applicant]: ApplicantType.Applicant,
  [GroupRole.CoSigner]: ApplicantType.CoSigner,
  [GroupRole.Occupant]: ApplicantType.Occupant
};

const APPLICANT_ROLE_LABELS: Record<ApplicantType, string> = {
  [ApplicantType.Applicant]: "primary applicant",
  [ApplicantType.CoSigner]: "guarantor",
  [ApplicantType.Occupant]: "occupant"
};

const formatNumberValue = (value: number) => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

const CURRENCY_FIELDS = new Set<FieldName>([
  FieldName.SelfReportedGrossIncome,
  FieldName.GrossIncome,
  FieldName.NetIncome,
  FieldName.UnpaidCollectionsBalance,
  FieldName.UnpaidCollectionsBalanceRental,
  FieldName.CollectionsBalance,
  FieldName.CollectionsBalanceRental
]);

const PERCENTAGE_FIELDS = new Set<FieldName>([
  FieldName.DebtToSelfReportedGrossIncome,
  FieldName.DebtToNetIncome,
  FieldName.DebtToGrossIncome
]);

/** Income-to-rent rules are expressed as a multiplier of rent (3x), not a percentage. */
const MULTIPLIER_FIELDS = new Set<FieldName>([
  FieldName.SelfReportedGrossIncomeToRentRatio,
  FieldName.GrossIncomeToRentRatio,
  FieldName.NetIncomeToRentRatio
]);

const parseNumeric = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getUnitByRule = (rule: Rule): "$" | "%" | "x" | "" => {
  if (CURRENCY_FIELDS.has(rule.field_name)) return "$";
  if (MULTIPLIER_FIELDS.has(rule.field_name)) return "x";
  if (
    PERCENTAGE_FIELDS.has(rule.field_name) ||
    rule.field_type === FieldType.Percentage
  )
    return "%";
  return "";
};

const formatRuleValueWithUnit = (rule: Rule, value: number): string => {
  const unit = getUnitByRule(rule);
  const formatted = formatNumberValue(value);

  if (unit === "$") return `${unit}${formatted}`;
  if (unit === "%" || unit === "x") return `${formatted}${unit}`;
  return formatted;
};
const getComparisonOperatorLabel = (
  leftValue: number,
  rightValue: number
): string => {
  if (leftValue > rightValue) return OPERATOR_LABELS[Operator.GreaterThan];
  if (leftValue < rightValue) return OPERATOR_LABELS[Operator.LessThan];
  return OPERATOR_LABELS[Operator.Equal];
};

const formatGroupRolesLabel = (roles: GroupRole[]): string => {
  if (roles.length === 0 || roles.includes(GroupRole.All))
    return GROUP_ROLE_LABELS[GroupRole.All];
  return roles.map((role) => `${GROUP_ROLE_LABELS[role]}s`).join(", ");
};

const formatDisplayedComputedValue = (rule: Rule) => {
  const parsedComputed = parseNumeric(rule.computed_value);
  if (parsedComputed !== null)
    return formatRuleValueWithUnit(rule, parsedComputed);

  const parsedThreshold = parseNumeric(rule.threshold);
  if (parsedThreshold !== null) return formatRuleValueWithUnit(rule, 0);

  return "-";
};

const formatDisplayedThreshold = (rule: Rule) => {
  const threshold = formatThreshold(rule);
  const parsedThreshold = parseNumeric(threshold);
  if (parsedThreshold !== null)
    return formatRuleValueWithUnit(rule, parsedThreshold);
  return threshold;
};

const formatDisplayedApplicantValue = (
  applicantValue: string | number,
  rule: Rule
) => {
  const parsedApplicantValue = parseNumeric(applicantValue);
  if (parsedApplicantValue !== null)
    return formatRuleValueWithUnit(rule, parsedApplicantValue);

  const parsedThreshold = parseNumeric(rule.threshold);
  if (parsedThreshold !== null) return formatRuleValueWithUnit(rule, 0);

  return applicantValue;
};

const getComparisonText = (rule: Rule): string => {
  const parsedComputed = parseNumeric(rule.computed_value);
  const parsedThreshold = parseNumeric(rule.threshold);

  if (parsedComputed !== null && parsedThreshold !== null)
    return `is ${getComparisonOperatorLabel(parsedComputed, parsedThreshold)} the`;

  if (OPERATOR_LABELS[rule.operator])
    return `is ${OPERATOR_LABELS[rule.operator]} the`;

  return "is compared to the";
};

const TIME_FRAME_VALUES = new Set<string>(Object.values(TimeFrame));

const getRuleTimeFrameLabel = (rule: Rule) => {
  if (!rule.field_type || !TIME_FRAME_VALUES.has(rule.field_type)) return "";
  return formatTitleFirstWord(rule.field_type);
};

const getRuleMetaLabel = (rule: Rule) => {
  const timeFrameLabel = getRuleTimeFrameLabel(rule);
  const groupTypeSource =
    rule.group_type || (timeFrameLabel ? undefined : rule.field_type);
  const groupTypeLabel = groupTypeSource
    ? formatTitleFirstWord(groupTypeSource)
    : "";
  const roleLabel = formatGroupRolesLabel(normalizeRuleGroupRoles(rule));

  const metaParts = [groupTypeLabel, roleLabel, timeFrameLabel].filter(Boolean);
  if (metaParts.length) return metaParts.join(", ");

  return formatConditionSubTitle(rule.field_name) || "";
};

const getRuleApplicantRows = (
  rule: Rule,
  applicants: Applicant[]
): ApplicantDetailRow[] => {
  if (!rule.per_applicant_results?.length) return [];

  const ruleRoles = normalizeRuleGroupRoles(rule);

  return rule.per_applicant_results
    .map((result) => {
      const applicant = applicants.find(
        (item) => item.id === result.applicant_id
      );
      if (!applicant?.role) return null;

      const roleMatches =
        ruleRoles.includes(GroupRole.All) ||
        ruleRoles.some((role) => ROLE_MAP[role] === applicant.role);
      if (!roleMatches) return null;

      return {
        id: result.applicant_id,
        name: getFullName({
          firstName: applicant.first_name,
          middleName: applicant.middle_name,
          lastName: applicant.last_name,
          short: true
        }),
        role: APPLICANT_ROLE_LABELS[applicant.role],
        value: formatThreshold({
          ...rule,
          threshold: result.value
        })
      };
    })
    .filter((row): row is ApplicantDetailRow => row !== null);
};

interface RuleConditionRowProps {
  rule: Rule;
  applicants: Applicant[];
  /** Summary card shows a Met/Not met badge and decline highlighting; the
   * "What to review" card renders the same row without the status column. */
  showStatusBadge: boolean;
  /** Render the decline state (red background + failed badge) when met. */
  isDecline?: boolean;
}

export const RuleConditionRow = ({
  rule,
  applicants,
  showStatusBadge,
  isDecline = false
}: RuleConditionRowProps) => {
  const [isApplicantDetailsOpened, setIsApplicantDetailsOpened] =
    useState(false);

  const applicantRows = getRuleApplicantRows(rule, applicants);
  const canOpenApplicantDetails = applicantRows.length > 0;
  const isDeclineMet = isDecline && Boolean(rule.result);

  return (
    <div className="py-2 border-b border-alpha-40 last:border-b-0">
      <div
        className={cx(
          "flex items-center justify-between gap-6 px-4 py-2",
          showStatusBadge && !rule.result && "rounded-lg bg-danger-50"
        )}
      >
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <Show if={canOpenApplicantDetails}>
            <button
              type="button"
              className="shrink-0"
              onClick={() => setIsApplicantDetailsOpened((prev) => !prev)}
            >
              <ChevronDown
                className={cx(
                  "w-4 shrink-0 text-content-700 transition-all",
                  isApplicantDetailsOpened ? "rotate-180" : "rotate-0"
                )}
              />
            </button>
          </Show>
          <div>
            <p className="text-xs font-medium text-content-1000 truncate">
              {formatConditionTitle(rule.field_name, rule.sub_categories)}
            </p>
            <p className="text-xs font-medium text-content-300 truncate mt-1">
              {getRuleMetaLabel(rule)}
            </p>
          </div>
        </div>

        <div
          className={cx(
            "grid items-center shrink-0 text-xs font-medium text-content-1000",
            showStatusBadge
              ? "grid-cols-[auto_64px_16px_53px]"
              : "grid-cols-[auto_64px]"
          )}
        >
          <p className="whitespace-nowrap">
            {formatDisplayedComputedValue(rule)} {getComparisonText(rule)}{" "}
            threshold:
          </p>
          <p className="w-16 text-right">{formatDisplayedThreshold(rule)}</p>
          <Show if={showStatusBadge}>
            <div />
            <div className="w-[53px] flex justify-center">
              <Badge
                className="w-full !items-center !justify-center"
                variant={
                  !isDeclineMet && rule.result
                    ? BadgeVariant.SUCCESS
                    : BadgeVariant.ERROR
                }
                size={BadgeSize.SM}
                leftElement={
                  !isDeclineMet && rule.result ? (
                    <Check className="w-3 h-3 shrink-0 stroke-success-700" />
                  ) : (
                    <FailedIcon className="w-3 h-3 shrink-0 text-danger-500" />
                  )
                }
              >
                {rule.result ? "Met" : "Not met"}
              </Badge>
            </div>
          </Show>
        </div>
      </div>

      <Show if={canOpenApplicantDetails && isApplicantDetailsOpened}>
        <div className="mt-2 bg-background-900 px-4 py-4 space-y-3">
          {applicantRows.map((applicantRow) => (
            <div
              key={applicantRow.id}
              className="flex items-center justify-between gap-3"
            >
              <p className="text-sm text-content-1000">
                {applicantRow.name}{" "}
                <span className="text-content-300">({applicantRow.role})</span>
              </p>
              {showStatusBadge ? (
                <div className="grid grid-cols-[64px_16px_53px] items-center shrink-0">
                  <p className="w-16 text-right text-sm text-content-300">
                    {formatDisplayedApplicantValue(applicantRow.value, rule)}
                  </p>
                  <div />
                  <div />
                </div>
              ) : (
                <p className="w-16 text-right text-sm text-content-300 shrink-0">
                  {formatDisplayedApplicantValue(applicantRow.value, rule)}
                </p>
              )}
            </div>
          ))}
        </div>
      </Show>
    </div>
  );
};
